import { Typography } from '@atlas-metrics/design-system';
import React from 'react';

import clsxm from '@/lib/clsxm';

import Accordion, { FREE_TEXT_MAX_LENGTH } from './Accordion';
import Badge from './Badge';
import HorizontalDivider from './HorizontalDivider';
import Icon from './Icon';
import VerticalDivider from './VerticalDivider';

export type IncompleteStatus = 'OMITTED' | 'INCOMPLETE';
export interface WidgetRowBaseProps {
  title: string | null;
  depthLevel?: 1 | 2 | 3;
  titleAsUnit?: boolean | undefined;
  hideHorizontalDivider?: boolean | undefined;
  accordion?: {
    toOpenText: string;
    toCloseText: string;
  };
}

export interface IncompleteRowProps extends WidgetRowBaseProps {
  incompleteStatus: IncompleteStatus;
  value: string;
}

export interface CompleteRowProps extends WidgetRowBaseProps {
  value?: unknown;
  unit?: string | undefined;
}

export type WidgetRowProps = IncompleteRowProps | CompleteRowProps;

const WidgetRow: React.FC<IncompleteRowProps | CompleteRowProps> = (props) => {
  const getDepthLevel = (depthLevel: number) => {
    if (depthLevel === 2) {
      return 'ml-4';
    } else if (depthLevel === 3) {
      return 'ml-6';
    }
  };

  const hasLongFreeText =
    'value' in props &&
    typeof props.value === 'string' &&
    props.value.length > FREE_TEXT_MAX_LENGTH;

  const hasVerticalDivider =
    props.depthLevel && props.depthLevel > 1 && !props.titleAsUnit;

  const getContent = () => {
    if ('incompleteStatus' in props) {
      return (
        <div className='flex items-center gap-2'>
          <Icon
            height={18}
            width={17}
            name={
              props.incompleteStatus === 'OMITTED' ? 'icon-cross' : 'icon-clock'
            }
            className='text-transparent'
          />
          <Typography variant='body-m' className='text-ds-grey-600'>
            {props.value}
          </Typography>
        </div>
      );
    }
    if (props.value == null) {
      // only rows with the highest level depth contain value
      return null;
    }
    return (
      <>
        {typeof props.value === 'string' && hasLongFreeText ? (
          <Accordion
            toOpenText={props.accordion?.toOpenText ?? 'Show more'}
            toCloseText={props.accordion?.toCloseText ?? 'Show less'}
            value={props.value}
          />
        ) : (
          <div className='flex items-center gap-2'>
            <Typography
              variant='body-m'
              className='whitespace-pre-line break-words text-ds-grey-700'
            >
              {props.value as string}
            </Typography>
            {props.unit != null && (
              <Badge variant='default' size='small'>
                {props.unit}
              </Badge>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={clsxm(
        'group flex flex-1 pt-4 hover:rounded-2xl hover:bg-ds-grey-50',
        'incompleteStatus' in props && 'rounded-2xl bg-ds-grey-50',
        props.title != null && props.value != null && 'pb-4 pt-4',
        props.title == null && props.value != null && 'pb-4 pt-2'
      )}
    >
      {hasVerticalDivider && (
        <VerticalDivider
          className={clsxm(
            'border-ds-grey-50 group-hover:border-ds-grey-400',
            getDepthLevel(props.depthLevel as number)
          )}
        />
      )}
      <div className={clsxm('flex flex-1 flex-col justify-between gap-2')}>
        {props.title != null && (
          <Typography
            variant='body-m'
            className={clsxm(
              'mr-4 p-0',
              !hasVerticalDivider && 'mx-4',
              props.depthLevel && 'text-ds-grey-950'
            )}
            bold
          >
            {props.title}
          </Typography>
        )}

        {!props.depthLevel && !props.hideHorizontalDivider && (
          <HorizontalDivider />
        )}
        <div className={clsxm('mr-4', !hasVerticalDivider && 'mx-4')}>
          {getContent()}
        </div>
      </div>
    </div>
  );
};

export default WidgetRow;
